/**
 * Configuration — Compliance
 *
 * One Compliance Rule per saved Licence, Badge or Ticket form.
 * Conditions, Response Period and fixed Compliance requirements live here.
 * Application fields are referenced from Licences, Badges & Tickets.
 * Decision Form FORM-000039 is stored in Form Inventory.
 */

import { formatConfigListDate } from './tradeCategoryConfig';
import { buildSampleTradeCategories } from '../sections/AdminDashboard/TradeCategories/tradeCategoriesDummyData';
import {
  LBT_ALL_TRADES_CATEGORY_ID,
  LBT_FIELD_CONTROL_OPTIONS,
  buildLbtSourceCatalog,
  getLbtItemTypeLabel,
} from './licensingCredentialRecords';
import {
  findComplianceItem,
  getComplianceItemTypeLabel,
} from './complianceItemsCatalog';
import { buildSampleFormInventory } from './formInventoryConfig';
import { getFormById } from './formInventoryConfigurationConfig';

export {
  formatConfigListDate,
  getComplianceItemTypeLabel as getComplianceTypeLabel,
  findComplianceItem,
};

export const COMPLIANCE_CONFIGURATION_ROUTE = '/admin/configuration/compliance';

export const COMPLIANCE_DECISION_FORM_ID = 'FORM-000039';
export const COMPLIANCE_DECISION_FORM_NAME = 'Compliance Decision Form';
export const COMPLIANCE_DECISION_FORM_DISPLAY = `${COMPLIANCE_DECISION_FORM_ID} — ${COMPLIANCE_DECISION_FORM_NAME}`;

export const CONDITION_OPERATOR_OPTIONS = [
  { value: 'equals', label: 'Equals' },
  { value: 'contains', label: 'Contains' },
];

export const REJECTION_BASIS_OPTIONS = [
  'Decision Requirements Not Met',
  'Information Unclear',
  'Information Does Not Match',
  'Not Confirmed by Issuing Body',
  'Information Not Provided',
];

export const APPROVAL_REQUIREMENT_ROWS = [
  { caseType: 'New', record: 'No existing record', result: 'Create Record' },
  { caseType: 'Renewal', record: 'Existing record', result: 'Replace Record with approved renewal details' },
  { caseType: 'Replacement', record: 'Existing record', result: 'Replace Record with approved replacement details' },
];

export const EFFECTIVE_FROM_ROWS = [
  { scenario: 'New', value: 'Confirmation date' },
  { scenario: 'Replacement', value: 'Confirmation date' },
  { scenario: 'Renewal: existing expiry is on or after confirmation', value: 'Day after the existing expiry date' },
  { scenario: 'Renewal: existing expiry is before confirmation', value: 'Confirmation date' },
  { scenario: 'Renewal: existing record has no expiry', value: 'Confirmation date' },
];

export const ASK_MORE_INFO_FIXED_ROWS = [
  { label: 'Entry', value: 'Ask More Info button inside FORM-000039' },
  { label: 'Communication', value: 'Linked Support Ticket' },
  { label: 'Starts When', value: 'The request first becomes available to the Business through that ticket' },
  { label: 'Deadline', value: 'Same local time after the case’s applied Response Period' },
  { label: 'Time Zone', value: 'Time zone recorded at first availability' },
  { label: 'Case', value: 'Continue the same Compliance case' },
];

/** Default Decision Checks shown on Queue / Archive from FORM-000039. */
export const DEFAULT_DECISION_CHECK_LABELS = [
  'Evidence readable',
  'Submitted details match evidence',
  'Holder details confirmed',
  'Number confirmed',
  'Issuer confirmed',
  'Jurisdiction confirmed',
  'Class and endorsements confirmed',
  'Dates confirmed',
  'State register checked',
  'Decision requirements completed',
];

export const TYPE_FILTER_OPTIONS = [
  { value: 'All', label: 'All' },
  { value: 'licence', label: 'Licence' },
  { value: 'badge', label: 'Badge' },
  { value: 'trade_ticket', label: 'Ticket' },
];

export const SAVE_SUCCESS_MESSAGE = 'Changes saved successfully.';
export const SAVE_FAILURE_MESSAGE =
  'Changes could not be saved. Review the highlighted fields and try again.';
export const DISCARD_CONFIRM_MESSAGE = 'Discard unsaved changes?';

export const CONFIG_LIST_ACTION_LABELS = {
  edit: 'Edit',
};

export const VALIDATION_MESSAGES = {
  sourceFormRequired: 'Select a Source Form.',
  sourceFormUnavailable: 'The selected Source Form is unavailable.',
  sourceFormLinked: 'A Compliance Rule already exists for this Source Form.',
  sourceFormLocked: 'Source Form cannot be changed after Save.',
  ruleIdChanged: 'Compliance Rule ID cannot be changed.',
  decisionCheckLabelRequired: 'Enter a Decision Check.',
  decisionCheckIncomplete: 'Complete every Decision Check before saving.',
  conditionIncomplete: 'Complete or remove this Condition.',
  conditionOperator: 'Select an operator available for this Field.',
  conditionValue: 'Enter or select a valid Value.',
  conditionUnavailable: 'Review this unavailable Field or Value.',
  conditionDuplicate: 'This Condition has already been added.',
  conditionsImpossible: 'These Conditions cannot match together.',
  responsePeriod: 'Enter a positive whole number of calendar days.',
  ruleChanged: 'This Rule has changed. Refresh before saving.',
  sourceChanged: 'The Source Form has changed. Refresh and review the Rule.',
  checksFailed: 'Checks could not be completed. Try again.',
};

export function mapLbtSourceToComplianceCatalogItem(item) {
  if (!item) return null;
  return {
    id: item.id,
    name: item.itemName,
    description: item.description || '',
    complianceType: item.itemType,
    formId: item.formId || '',
    tradeCategoryId: item.tradeCategoryId || '',
    iconUrl: item.iconUrl || '',
    iconFileName: item.iconFileName || '',
    iconLabel: item.iconLabel || '',
    formInputs: Array.isArray(item.formInputs) ? item.formInputs : [],
    status: 'active',
    createdOn: item.createdOn || '',
    createdBy: item.createdBy || '',
    lastUpdatedOn: item.lastUpdatedOn || '',
    lastUpdatedBy: item.lastUpdatedBy || '',
  };
}

export function buildComplianceItemsCatalogFromLbt(sourceItems, forms, tradeCategories) {
  const inventory = Array.isArray(forms) ? forms : [];
  const categories = Array.isArray(tradeCategories)
    ? tradeCategories
    : buildSavedTradeCategoryRecords();
  const items = Array.isArray(sourceItems)
    ? sourceItems
    : buildLbtSourceCatalog(inventory, categories);
  return items.map(mapLbtSourceToComplianceCatalogItem).filter(Boolean);
}

export function formatComplianceSourceOptionLabel(item) {
  if (!item) return '';
  const name = item.name || item.itemName || '';
  const id = item.id || '';
  if (!id) return name;
  return `${id} — ${name}`;
}

export function findComplianceDecisionForm(forms = []) {
  return (
    (forms || []).find(
      (form) =>
        form.id === COMPLIANCE_DECISION_FORM_ID ||
        form.formKey === 'compliance_decision_form' ||
        form.implementedFormKey === 'compliance_decision_form' ||
        String(form.formName || '').trim().toLowerCase() === COMPLIANCE_DECISION_FORM_NAME.toLowerCase(),
    ) || null
  );
}

export function formatComplianceDecisionFormDisplay(forms = []) {
  const form = findComplianceDecisionForm(forms);
  if (form?.id && form?.formName) return `${form.id} — ${form.formName}`;
  return COMPLIANCE_DECISION_FORM_DISPLAY;
}

export function getDecisionFormChecklist(forms = []) {
  const form = findComplianceDecisionForm(forms);
  const fields = getFetchedFormFields(form);
  if (fields.length) {
    return fields.map((field) => ({
      id: field.id,
      label: field.fieldLabel,
      mandatory: field.fieldLabel !== 'State register checked',
    }));
  }
  return DEFAULT_DECISION_CHECK_LABELS.map((label, index) => ({
    id: `DC-${String(index + 1).padStart(3, '0')}`,
    label,
    mandatory: label !== 'State register checked',
  }));
}

export function getComplianceTradeCategoryFilterOptions(tradeCategories = []) {
  const options = [{ value: 'All', label: 'All' }];
  const seen = new Set();
  const add = (value, label) => {
    if (!value || seen.has(value)) return;
    seen.add(value);
    options.push({ value, label });
  };
  add(LBT_ALL_TRADES_CATEGORY_ID, 'All Trades');
  (tradeCategories || []).forEach((row) => add(row.id, row.name));
  return options;
}

/** Saved Trade Category records from Trades Poster — Trade Categories. */
export function buildSavedTradeCategoryRecords() {
  return buildSampleTradeCategories().map((c) => ({
    id: c.id,
    name: c.name,
  }));
}

export function findSavedTradeCategory(tradeCategories, id) {
  if (!id) return null;
  return (tradeCategories || []).find((row) => row.id === id) ?? null;
}

export function getTradeCategoryName(tradeCategories, tradeCategoryId) {
  if (!tradeCategoryId) return '—';
  if (tradeCategoryId === LBT_ALL_TRADES_CATEGORY_ID || tradeCategoryId === 'ALL_TRADES') {
    return 'All Trades';
  }
  return findSavedTradeCategory(tradeCategories, tradeCategoryId)?.name || '—';
}

export function getListTradeCategoryDisplay(config, tradeCategories) {
  const ids = normalizeTradeCategoryIds(config?.tradeCategoryIds);
  if (ids.length === 0) return { text: '—', truncated: false, names: [] };
  const names = ids
    .map((id) => getTradeCategoryName(tradeCategories, id))
    .filter((name) => name && name !== '—');
  if (names.length === 0) return { text: '—', truncated: false, names: [] };
  return { text: names[0], truncated: names.length > 1, names };
}

export function getRuleTradeCategoryLabel(config, tradeCategories) {
  return getListTradeCategoryDisplay(config, tradeCategories).text || '—';
}

export function normalizeTradeCategoryIds(ids) {
  const list = Array.isArray(ids) ? ids : [];
  const seen = new Set();
  const out = [];
  list.forEach((id) => {
    const trimmed = String(id || '').trim();
    if (!trimmed || seen.has(trimmed)) return;
    seen.add(trimmed);
    out.push(trimmed);
  });
  return out;
}

export function createEmptyCondition(overrides = {}) {
  return {
    id: '',
    fieldId: '',
    fieldLabel: '',
    fieldControl: '',
    operator: '',
    value: '',
    values: [],
    unavailable: false,
    lastAvailableLabel: '',
    ...overrides,
  };
}

export function createEmptyDecisionCheck(overrides = {}) {
  return {
    id: '',
    label: '',
    mandatory: true,
    conditions: [],
    ...overrides,
  };
}

export function createDefaultDecisionChecks() {
  return DEFAULT_DECISION_CHECK_LABELS.map((label, index) =>
    createEmptyDecisionCheck({
      id: `DC-TEMP-${index + 1}`,
      label,
      mandatory: true,
      conditions: [],
    }),
  );
}

export function createDefaultCaseActions() {
  return {
    approve: {
      newSubmission: 'Create Record',
      renewal: 'Replace Record',
      replacement: 'Replace Record',
      effectiveDateNew: 'Confirmation date',
      effectiveDateReplacement: 'Confirmation date',
      effectiveDateRenewalBeforeExpiry: 'Day after the existing expiry date',
      effectiveDateRenewalAfterExpiry: 'Confirmation date',
    },
    reject: {
      noExistingRecord: 'No Record',
      existingRecord: 'Keep Current Record',
      bases: [...REJECTION_BASIS_OPTIONS],
    },
    askMoreInfo: {
      responsePeriodDays: 14,
      communication: 'Linked Support Ticket',
      caseStatus: 'Awaiting Information',
      userCommunication: 'Support Ticket Inbox',
      notifications: 'Dashboard · Notifications · Email',
    },
    referToFraud: {
      transferReasonRequired: true,
      linkedCase: 'Fraud & Security Case',
      complianceDecisionOnTransfer: 'None',
      businessRecordActionOnTransfer: 'None',
    },
  };
}

export function emptySavedSnapshot() {
  return {
    formId: '',
    tradeCategoryIds: [],
    conditions: [],
    responsePeriodDays: 14,
    decisionChecks: createDefaultDecisionChecks(),
    caseActions: createDefaultCaseActions(),
    complianceType: '',
    complianceItem: '',
  };
}

export function normalizeConditionValueList(condition) {
  if (Array.isArray(condition?.values) && condition.values.length) {
    return condition.values.map((value) => String(value).trim()).filter(Boolean);
  }
  if (Array.isArray(condition?.value)) {
    return condition.value.map((value) => String(value).trim()).filter(Boolean);
  }
  const single = String(condition?.value || '').trim();
  return single ? [single] : [];
}

export function normalizeCondition(condition) {
  const operator =
    condition?.operator === 'contains'
      ? 'contains'
      : condition?.operator === 'equals'
        ? 'equals'
        : '';
  const values = normalizeConditionValueList(condition);
  return {
    id: condition?.id || '',
    fieldId: String(condition?.fieldId || '').trim(),
    fieldLabel: String(condition?.fieldLabel || condition?.lastAvailableLabel || '').trim(),
    fieldControl: String(condition?.fieldControl || '').trim(),
    operator,
    value: values[0] || '',
    values,
    unavailable: Boolean(condition?.unavailable),
    lastAvailableLabel: String(
      condition?.lastAvailableLabel || condition?.fieldLabel || '',
    ).trim(),
  };
}

export function normalizeDecisionCheck(check) {
  return {
    id: check?.id || '',
    label: String(check?.label || '').trim(),
    mandatory: check?.mandatory !== false,
    conditions: (check?.conditions || []).map(normalizeCondition),
  };
}

function conditionsFromLegacyChecks(checks = []) {
  const nested = [];
  (checks || []).forEach((check) => {
    (check.conditions || []).forEach((condition) => nested.push(normalizeCondition(condition)));
  });
  return nested;
}

export function normalizeSavedSnapshot(snapshot, complianceType, complianceItem) {
  const base = snapshot || emptySavedSnapshot();
  const caseActions = {
    ...createDefaultCaseActions(),
    ...(base.caseActions || {}),
    askMoreInfo: {
      ...createDefaultCaseActions().askMoreInfo,
      ...(base.caseActions?.askMoreInfo || {}),
    },
    reject: {
      ...createDefaultCaseActions().reject,
      ...(base.caseActions?.reject || {}),
      bases: Array.isArray(base.caseActions?.reject?.bases)
        ? base.caseActions.reject.bases
        : [...REJECTION_BASIS_OPTIONS],
    },
  };
  const conditions = Array.isArray(base.conditions)
    ? base.conditions.map(normalizeCondition)
    : conditionsFromLegacyChecks(base.decisionChecks);
  const responsePeriodDays = Number(
    base.responsePeriodDays ?? caseActions.askMoreInfo?.responsePeriodDays ?? 14,
  );
  caseActions.askMoreInfo.responsePeriodDays = responsePeriodDays;
  return {
    formId: String(base.formId || '').trim(),
    tradeCategoryIds: normalizeTradeCategoryIds(base.tradeCategoryIds),
    conditions,
    responsePeriodDays,
    decisionChecks: (base.decisionChecks || createDefaultDecisionChecks()).map(normalizeDecisionCheck),
    caseActions,
    complianceType: complianceType || base.complianceType || '',
    complianceItem: complianceItem || base.complianceItem || '',
  };
}

export function countDecisionChecks(config) {
  return getSavedSnapshot(config).decisionChecks.length;
}

export function countConditions(config) {
  if (Array.isArray(config?.conditions)) return config.conditions.length;
  return getSavedSnapshot(config).conditions.length;
}

export function getResponsePeriodDays(config) {
  const saved = getSavedSnapshot(config);
  const n = Number(
    config?.responsePeriodDays ?? saved.responsePeriodDays ?? saved.caseActions?.askMoreInfo?.responsePeriodDays,
  );
  return Number.isFinite(n) && n > 0 ? n : 14;
}

export function formatResponsePeriod(days) {
  const n = Number(days);
  if (!Number.isFinite(n) || n <= 0) return '—';
  return `${n} calendar day${n === 1 ? '' : 's'}`;
}

export function getSavedSnapshot(config) {
  return normalizeSavedSnapshot(
    config?.saved,
    config?.complianceType,
    config?.complianceItem,
  );
}

export function formatComplianceConfigDateTime(value) {
  return formatConfigListDate(value);
}

export function isPositiveWholeNumber(value) {
  return /^[1-9]\d*$/.test(String(value ?? '').trim());
}

export function mapFieldControlToKind(control) {
  const value = String(control || '').trim().toLowerCase();
  if (!value) return '';
  if (value.includes('file') || value.includes('photo') || value.includes('upload')) return 'file';
  if (value.includes('date')) return 'date';
  if (value.includes('multi-select')) return 'multi';
  if (
    value.includes('single-select') ||
    value.includes('dropdown') ||
    value.includes('radio')
  ) {
    return 'single';
  }
  if (value.includes('multi-line') || value.includes('textarea')) return 'text';
  if (LBT_FIELD_CONTROL_OPTIONS.includes(control) || value.includes('text')) return 'text';
  return 'text';
}

export function operatorsForKind(kind) {
  if (kind === 'text') {
    return [
      { value: 'equals', label: 'Equals' },
      { value: 'contains', label: 'Contains' },
    ];
  }
  if (kind === 'single' || kind === 'date') {
    return [{ value: 'equals', label: 'Equals' }];
  }
  if (kind === 'multi') {
    return [
      { value: 'contains', label: 'Contains' },
      { value: 'equals', label: 'Equals' },
    ];
  }
  return [];
}

function optionsFromFormField(formField) {
  const values = formField?.fixedFormValues;
  if (!Array.isArray(values)) return [];
  return values.map((value) => String(value).trim()).filter(Boolean);
}

export function getConditionFieldsForSource(catalogItem, form) {
  const formFields = getFetchedFormFields(form);
  if (formFields.length) {
    return formFields
      .map((field) => {
        const kind = mapFieldControlToKind(field.controlType);
        if (!kind || kind === 'file') return null;
        return {
          id: field.id,
          fieldLabel: field.fieldLabel,
          fieldControl: field.controlType,
          kind,
          options: optionsFromFormField(field),
        };
      })
      .filter(Boolean);
  }

  const fromInputs = Array.isArray(catalogItem?.formInputs) ? catalogItem.formInputs : [];
  return fromInputs
    .map((input) => {
      const kind = mapFieldControlToKind(input.fieldControl);
      if (!kind || kind === 'file') return null;
      return {
        id: input.id,
        fieldLabel: input.fieldLabel,
        fieldControl: input.fieldControl,
        kind,
        options: [],
      };
    })
    .filter(Boolean);
}

export function getSourceApplicationFields(catalogItem, form) {
  const formFields = getFetchedFormFields(form);
  if (formFields.length) {
    return formFields.map((field) => ({
      id: field.id,
      fieldLabel: field.fieldLabel,
      fieldControl: field.controlType,
      required:
        field.baseRequirement === 'Optional'
          ? 'Optional'
          : field.baseRequirement === 'Required'
            ? 'Required'
            : 'Required',
    }));
  }
  const fromInputs = Array.isArray(catalogItem?.formInputs) ? catalogItem.formInputs : [];
  return fromInputs.map((input) => ({
    id: input.id,
    fieldLabel: input.fieldLabel,
    fieldControl: input.fieldControl,
    required: input.required,
  }));
}

export function annotateConditionAvailability(condition, fields = []) {
  const normalized = normalizeCondition(condition);
  const field = (fields || []).find((row) => row.id === normalized.fieldId) || null;
  if (!normalized.fieldId) return { ...normalized, unavailable: false };
  if (!field) {
    return { ...normalized, unavailable: true, lastAvailableLabel: normalized.fieldLabel || normalized.lastAvailableLabel };
  }
  const allowedOps = operatorsForKind(field.kind).map((row) => row.value);
  if (normalized.operator && !allowedOps.includes(normalized.operator)) {
    return {
      ...normalized,
      fieldLabel: field.fieldLabel,
      fieldControl: field.fieldControl,
      unavailable: true,
      lastAvailableLabel: field.fieldLabel,
    };
  }
  if ((field.kind === 'single' || field.kind === 'multi') && (field.options || []).length) {
    const optionSet = new Set(field.options);
    const missing = (normalized.values || []).some((value) => !optionSet.has(value));
    if (missing) {
      return {
        ...normalized,
        fieldLabel: field.fieldLabel,
        fieldControl: field.fieldControl,
        unavailable: true,
        lastAvailableLabel: field.fieldLabel,
      };
    }
  }
  return {
    ...normalized,
    fieldLabel: field.fieldLabel,
    fieldControl: field.fieldControl,
    unavailable: false,
    lastAvailableLabel: field.fieldLabel,
  };
}

export function conditionKey(condition) {
  const normalized = normalizeCondition(condition);
  const valueKey = [...normalized.values].sort().join('\u0001');
  return [normalized.fieldId, normalized.operator, valueKey].join('|');
}

export function findImpossibleConditionIndexes(conditions = [], fields = []) {
  const conflict = new Set();
  const byField = new Map();
  (conditions || []).forEach((condition, index) => {
    const normalized = normalizeCondition(condition);
    if (!normalized.fieldId || !normalized.operator || !normalized.values.length) return;
    const list = byField.get(normalized.fieldId) || [];
    list.push({ ...normalized, index });
    byField.set(normalized.fieldId, list);
  });

  byField.forEach((list, fieldId) => {
    const field = (fields || []).find((row) => row.id === fieldId);
    const kind = field?.kind || mapFieldControlToKind(list[0].fieldControl);
    const equals = list.filter((row) => row.operator === 'equals');
    const contains = list.filter((row) => row.operator === 'contains');

    if (kind === 'text' || kind === 'single' || kind === 'date') {
      const eqValues = equals.map((row) => row.values[0] || '');
      if (new Set(eqValues.filter(Boolean)).size > 1) {
        equals.forEach((row) => conflict.add(row.index));
      }
      if (kind === 'text' && eqValues.length) {
        contains.forEach((row) => {
          const needle = row.values[0] || '';
          if (!needle) return;
          const canMatch = eqValues.every((value) => value.includes(needle));
          if (!canMatch) {
            conflict.add(row.index);
            equals.forEach((eq) => conflict.add(eq.index));
          }
        });
      }
    }

    if (kind === 'multi') {
      const eqSets = equals.map((row) => new Set(row.values));
      if (eqSets.length > 1) {
        const first = eqSets[0];
        const same = eqSets.every(
          (set) => set.size === first.size && [...set].every((value) => first.has(value)),
        );
        if (!same) equals.forEach((row) => conflict.add(row.index));
      }
      if (eqSets.length === 1) {
        contains.forEach((row) => {
          const needle = row.values[0] || '';
          if (needle && !eqSets[0].has(needle)) {
            conflict.add(row.index);
            equals.forEach((eq) => conflict.add(eq.index));
          }
        });
      }
    }
  });

  return conflict;
}

function isConditionComplete(condition, field) {
  const normalized = normalizeCondition(condition);
  if (!normalized.fieldId || !normalized.operator) return false;
  if (!field) return Boolean(normalized.values.length);
  if (field.kind === 'text') {
    return Boolean(normalized.values[0] && /\S/.test(normalized.values[0]));
  }
  if (field.kind === 'date' || field.kind === 'single') {
    return Boolean(normalized.values[0]);
  }
  if (field.kind === 'multi') {
    if (normalized.operator === 'equals') return normalized.values.length > 0;
    return Boolean(normalized.values[0]);
  }
  return Boolean(normalized.values.length);
}

export function ruleFingerprint(draft) {
  return JSON.stringify({
    complianceItemId: draft?.complianceItemId || '',
    conditions: (draft?.conditions || []).map(normalizeCondition),
    responsePeriodDays: String(draft?.responsePeriodDays ?? ''),
  });
}

export function sourceFingerprint(catalogItem) {
  return JSON.stringify({
    id: catalogItem?.id || '',
    formId: catalogItem?.formId || '',
    name: catalogItem?.name || '',
    description: catalogItem?.description || '',
    tradeCategoryId: catalogItem?.tradeCategoryId || '',
    lastUpdatedOn: catalogItem?.lastUpdatedOn || '',
    formInputs: catalogItem?.formInputs || [],
  });
}

/**
 * Enrich configuration with live catalogue values (name, type, icon, formId).
 */
export function enrichConfigFromSource(config, catalogItem) {
  if (!config) return config;
  if (!catalogItem) {
    return {
      ...config,
      sourceUnavailable: true,
    };
  }

  const saved = getSavedSnapshot(config);
  const tradeCategoryIds = catalogItem.tradeCategoryId
    ? [catalogItem.tradeCategoryId]
    : saved.tradeCategoryIds;
  return {
    ...config,
    complianceItemId: catalogItem.id,
    complianceType: catalogItem.complianceType,
    complianceItem: catalogItem.name,
    description: catalogItem.description || config.description || '',
    formId: catalogItem.formId || saved.formId || '',
    tradeCategoryIds,
    iconUrl: catalogItem.iconUrl || '',
    iconFileName: catalogItem.iconFileName || '',
    iconLabel: catalogItem.iconLabel || '',
    conditions: saved.conditions,
    responsePeriodDays: saved.responsePeriodDays,
    sourceUnavailable: false,
    saved: normalizeSavedSnapshot(
      {
        ...saved,
        formId: catalogItem.formId || saved.formId || '',
        tradeCategoryIds,
        complianceType: catalogItem.complianceType,
        complianceItem: catalogItem.name,
      },
      catalogItem.complianceType,
      catalogItem.name,
    ),
  };
}

export function resolveLinkedForm(catalogItem, forms) {
  const formId = catalogItem?.formId || '';
  if (!formId) return null;
  return getFormById(forms || [], formId);
}

export function getFetchedFormFields(form) {
  return (form?.fields || []).filter((field) => field?.status !== 'Inactive');
}

export function nextDecisionCheckId(checks = []) {
  let max = 0;
  checks.forEach((check) => {
    const match = String(check.id || '').match(/^DC-(\d+)$/i);
    if (match) max = Math.max(max, parseInt(match[1], 10));
  });
  return `DC-${String(max + 1).padStart(3, '0')}`;
}

export function nextConditionId(checks = []) {
  let max = 0;
  const visit = (condition) => {
    const match = String(condition?.id || '').match(/^CND-(\d+)$/i);
    if (match) max = Math.max(max, parseInt(match[1], 10));
  };
  (checks || []).forEach((row) => {
    if (Array.isArray(row?.conditions)) {
      row.conditions.forEach(visit);
    } else {
      visit(row);
    }
  });
  return `CND-${String(max + 1).padStart(3, '0')}`;
}

export function collectSaveValidationErrors(draft, {
  isFirstSave,
  existingConfigs,
  excludeConfigId,
  lockedComplianceItemId,
  catalogItem,
  conditionFields,
  openedRuleFingerprint,
  openedSourceFingerprint,
  currentSourceFingerprint,
} = {}) {
  const errors = {};

  if (isFirstSave) {
    if (!draft.complianceItemId) {
      errors.complianceItemId = VALIDATION_MESSAGES.sourceFormRequired;
    } else if (!catalogItem) {
      errors.complianceItemId = VALIDATION_MESSAGES.sourceFormUnavailable;
    } else {
      const linked = (existingConfigs || []).some(
        (row) =>
          row.id !== excludeConfigId &&
          row.complianceItemId === draft.complianceItemId,
      );
      if (linked) {
        errors.complianceItemId = VALIDATION_MESSAGES.sourceFormLinked;
      }
    }
  } else if (
    lockedComplianceItemId &&
    draft.complianceItemId &&
    draft.complianceItemId !== lockedComplianceItemId
  ) {
    errors.complianceItemId = VALIDATION_MESSAGES.sourceFormLocked;
  } else if (draft.complianceItemId && !catalogItem) {
    errors.complianceItemId = VALIDATION_MESSAGES.sourceFormUnavailable;
  }

  if (!isFirstSave && draft.id && excludeConfigId && draft.id !== excludeConfigId) {
    errors.id = VALIDATION_MESSAGES.ruleIdChanged;
  }

  if (!isPositiveWholeNumber(draft.responsePeriodDays)) {
    errors.responsePeriodDays = VALIDATION_MESSAGES.responsePeriod;
  }

  const fields = conditionFields || [];
  const conditions = Array.isArray(draft.conditions) ? draft.conditions : [];

  const flat = [];
  conditions.forEach((condition, conditionIndex) => {
    const key = `conditions.${conditionIndex}`;
    const annotated = annotateConditionAvailability(condition, fields);
    const field = fields.find((row) => row.id === annotated.fieldId) || null;
    const allowedOps = field ? operatorsForKind(field.kind).map((row) => row.value) : [];
    flat.push({ annotated, field, key, index: flat.length });

    if (!annotated.fieldId || !annotated.operator || !isConditionComplete(annotated, field)) {
      errors[key] = VALIDATION_MESSAGES.conditionIncomplete;
      return;
    }
    if (field && annotated.operator && allowedOps.length && !allowedOps.includes(annotated.operator)) {
      errors[key] = VALIDATION_MESSAGES.conditionOperator;
      return;
    }
    if (field?.kind === 'date' && annotated.values[0] && Number.isNaN(new Date(annotated.values[0]).getTime())) {
      errors[key] = VALIDATION_MESSAGES.conditionValue;
      return;
    }
    if (annotated.unavailable) {
      errors[key] = VALIDATION_MESSAGES.conditionUnavailable;
    }
  });

  const seen = new Set();
  flat.forEach((row) => {
    if (errors[row.key]) return;
    const dupKey = conditionKey(row.annotated);
    if (seen.has(dupKey)) {
      errors[row.key] = VALIDATION_MESSAGES.conditionDuplicate;
      return;
    }
    seen.add(dupKey);
  });

  const impossible = findImpossibleConditionIndexes(
    flat.map((row) => row.annotated),
    fields,
  );
  if (impossible.size > 0) {
    errors.conditionsImpossible = VALIDATION_MESSAGES.conditionsImpossible;
    impossible.forEach((index) => {
      const key = flat[index]?.key;
      if (key && !errors[key]) errors[key] = VALIDATION_MESSAGES.conditionsImpossible;
    });
  }

  if (openedRuleFingerprint && excludeConfigId) {
    const live = (existingConfigs || []).find((row) => row.id === excludeConfigId);
    if (live && ruleFingerprint(draftFromLiveRule(live)) !== openedRuleFingerprint) {
      errors.ruleChanged = VALIDATION_MESSAGES.ruleChanged;
    }
  }

  if (
    openedSourceFingerprint &&
    currentSourceFingerprint &&
    openedSourceFingerprint !== currentSourceFingerprint
  ) {
    errors.sourceChanged = VALIDATION_MESSAGES.sourceChanged;
  }

  return errors;
}

function draftFromLiveRule(config) {
  const saved = getSavedSnapshot(config);
  return {
    complianceItemId: config.complianceItemId || '',
    conditions: saved.conditions,
    responsePeriodDays: saved.responsePeriodDays,
  };
}

export function getComplianceConfigurationListActions() {
  return ['edit'];
}

export function nextComplianceConfigurationId(configs) {
  let max = 0;
  (configs || []).forEach((row) => {
    const match = String(row.id || '').match(/^COM-(\d+)$/i);
    if (match) max = Math.max(max, parseInt(match[1], 10));
  });
  return `COM-${String(max + 1).padStart(4, '0')}`;
}

export function findComplianceConfiguration(configs, id) {
  if (!id) return null;
  return (configs || []).find((row) => row.id === id) ?? null;
}

export function getUnlinkedComplianceItems(catalog, configs, { includeItemId } = {}) {
  const linked = new Set(
    (configs || [])
      .map((row) => row.complianceItemId)
      .filter(Boolean),
  );
  return (catalog || []).filter((item) => {
    if (includeItemId && item.id === includeItemId) return true;
    return !linked.has(item.id);
  });
}

export function matchesComplianceConfigurationSearch(config, query) {
  const q = String(query || '').trim().toLowerCase();
  if (!q) return true;
  const haystack = [config.id, config.complianceItemId, config.complianceItem]
    .join(' ')
    .toLowerCase();
  return haystack.includes(q);
}

export function matchesComplianceConfigurationFilters(config, filters) {
  const {
    typeFilter = 'All',
    tradeCategoryFilter = 'All',
  } = filters || {};

  if (typeFilter !== 'All' && config.complianceType !== typeFilter) return false;

  if (tradeCategoryFilter !== 'All') {
    const ids = normalizeTradeCategoryIds(config?.tradeCategoryIds);
    if (!ids.includes(tradeCategoryFilter)) return false;
  }
  return true;
}

export function sortComplianceConfigurations(configs) {
  return [...(configs || [])].sort((a, b) => {
    const aTime = new Date(a.lastUpdatedOn || a.lastSavedOn || 0).getTime();
    const bTime = new Date(b.lastUpdatedOn || b.lastSavedOn || 0).getTime();
    if (Number.isFinite(bTime) && Number.isFinite(aTime) && bTime !== aTime) return bTime - aTime;
    return String(b.id || '').localeCompare(String(a.id || ''), undefined, { numeric: true });
  });
}

function sampleStamp(day, month, year, hour, minute) {
  return new Date(year, month - 1, day, hour, minute, 0, 0).toISOString();
}

export function buildSampleComplianceConfigurations(catalog, forms) {
  const source = catalog || buildComplianceItemsCatalogFromLbt(undefined, forms || buildSampleFormInventory());
  const item = (id) => findComplianceItem(source, id);

  const samples = [
    {
      id: 'COM-0001',
      complianceItemId: 'LIC-0002',
      createdOn: sampleStamp(12, 8, 2026, 9, 0),
      lastUpdatedOn: sampleStamp(20, 8, 2026, 11, 15),
    },
    {
      id: 'COM-0002',
      complianceItemId: 'LIC-0001',
      createdOn: sampleStamp(12, 8, 2026, 9, 20),
      lastUpdatedOn: sampleStamp(20, 8, 2026, 10, 40),
    },
    {
      id: 'COM-0003',
      complianceItemId: 'BDG-0001',
      createdOn: sampleStamp(13, 8, 2026, 10, 15),
      lastUpdatedOn: sampleStamp(19, 8, 2026, 15, 20),
    },
    {
      id: 'COM-0004',
      complianceItemId: 'TKT-0001',
      createdOn: sampleStamp(14, 8, 2026, 8, 45),
      lastUpdatedOn: sampleStamp(18, 8, 2026, 13, 45),
    },
  ];

  return samples.map((row) => {
    const catalogItem = item(row.complianceItemId);
    const formId = catalogItem?.formId || '';
    const tradeCategoryIds = catalogItem?.tradeCategoryId ? [catalogItem.tradeCategoryId] : [];
    const saved = normalizeSavedSnapshot(
      {
        formId,
        tradeCategoryIds,
        conditions: [],
        responsePeriodDays: 14,
        decisionChecks: createDefaultDecisionChecks(),
        caseActions: createDefaultCaseActions(),
        complianceType: catalogItem?.complianceType || '',
        complianceItem: catalogItem?.name || '',
      },
      catalogItem?.complianceType,
      catalogItem?.name,
    );
    const base = {
      id: row.id,
      complianceItemId: row.complianceItemId,
      complianceType: catalogItem?.complianceType || '',
      complianceItem: catalogItem?.name || '',
      description: catalogItem?.description || '',
      formId,
      tradeCategoryIds,
      conditions: [],
      responsePeriodDays: 14,
      saved,
      createdOn: row.createdOn,
      createdBy: 'Olivia Brown',
      lastSavedOn: row.lastUpdatedOn,
      lastSavedBy: 'Olivia Brown',
      lastUpdatedOn: row.lastUpdatedOn,
      lastUpdatedBy: 'Olivia Brown',
    };
    return enrichConfigFromSource(base, catalogItem);
  });
}
